import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Card, CardBody, Col, Nav, NavItem, NavLink, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

export type ChartData = {
  labels: string[];
  series: number[];
  values: number[];
};

export type ChartSeries = number[] | { name: string; data: number[] }[];

export type ChartCardConfig = {
  id: string;
  title: string;
  subtitle?: string;
  type: "donut" | "polarArea" | "radialBar" | "bar" | "line";
  height: number;
  series: ChartSeries;
  options: ApexOptions;
  hasData: boolean;
  emptyMessage?: string;
  colProps?: Partial<Record<"xs" | "sm" | "md" | "lg" | "xl" | "xxl", number>>;
  cardClassName?: string;
  bodyClassName?: string;
};

export type ChartTabConfig = {
  id: string;
  label: string;
  charts: ChartCardConfig[];
};

interface ChartTabsSectionProps {
  tabs: ChartTabConfig[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  navClassName?: string;
  isLoading?: boolean;
  loadingMessage?: string;
}

const ChartTabsSection = ({
  tabs,
  activeTab,
  onTabChange,
  navClassName,
  isLoading = false,
  loadingMessage = "Cargando métricas...",
}: ChartTabsSectionProps) => {
  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, nextTab: string) => {
    event.preventDefault();
    onTabChange(nextTab);
  };

  const navClasses = classNames("nav nav-tabs nav-border-top mb-3", navClassName);
  const activeConfig = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];
  const chartsToRender = useMemo(() => activeConfig?.charts ?? [], [activeConfig]);

  const chunkCharts = useMemo(() => {
    const slides: ChartCardConfig[][] = [];
    for (let index = 0; index < chartsToRender.length; index += 2) {
      slides.push(chartsToRender.slice(index, index + 2));
    }
    return slides;
  }, [chartsToRender]);

  const [activeSlide, setActiveSlide] = useState(0);
  useEffect(() => {
    setActiveSlide(0);
  }, [activeConfig?.id, chartsToRender.length]);

  const totalSlides = chunkCharts.length;
  const showCarousel = totalSlides > 1;

  const nextSlide = () => {
    if (!showCarousel) return;
    setActiveSlide((prev) => (prev + 1) % totalSlides);
  };

  const previousSlide = () => {
    if (!showCarousel) return;
    setActiveSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  const goToIndex = (newIndex: number) => {
    if (!showCarousel || newIndex === activeSlide) return;
    setActiveSlide(newIndex);
  };

  const renderChartsRow = (charts: ChartCardConfig[], slideKey?: string | number) => (
    <Row className="g-3" key={slideKey ?? `${activeConfig?.id}-row`}>
      {charts.map((chart) => (
        <Col key={`${activeConfig?.id}-${chart.id}`} {...(chart.colProps ?? { xs: 12, lg: 6 })}>
          <Card className={classNames("h-100 border-0 shadow-sm", chart.cardClassName)}>
            <CardBody className={chart.bodyClassName}>
              <div className="d-flex align-items-center mb-3">
                <div className="flex-grow-1">
                  <h6 className="mb-0">{chart.title}</h6>
                  {chart.subtitle ? <small className="text-muted">{chart.subtitle}</small> : null}
                </div>
              </div>
              {chart.hasData ? (
                <ReactApexChart
                  dir="ltr"
                  className="apex-charts"
                  type={chart.type}
                  height={chart.height}
                  series={chart.series}
                  options={chart.options}
                />
              ) : (
                <div className="text-muted text-center">
                  {chart.emptyMessage ?? "Sin información suficiente"}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );

  if (tabs.length === 0) {
    return null;
  }

  return (
    <>
      <Nav tabs className={navClasses}>
        {tabs.map((tab) => (
          <NavItem key={tab.id}>
            <NavLink
              href="#"
              className={classNames({ active: activeConfig?.id === tab.id })}
              style={{ cursor: "pointer" }}
              onClick={(event) => handleNavClick(event, tab.id)}
            >
              {tab.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      {isLoading ? (
        <Row className="g-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <Col key={`chart-skeleton-${index}`} xs={12} lg={6}>
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <div className="skeleton-pulse skeleton-line mb-2" style={{ width: "65%" }} />
                  <div className="skeleton-pulse skeleton-line mb-4" style={{ width: "40%", height: 14 }} />
                  <div className="skeleton-pulse skeleton-rect" style={{ width: "100%", height: 220 }} />
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      ) : showCarousel ? (
        <>
          {renderChartsRow(chunkCharts[activeSlide], `slide-row-${activeSlide}`)}
          <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
            {chunkCharts.map((_, index) => (
              <button
                key={`indicator-${index}`}
                type="button"
                className={classNames("btn btn-sm rounded-pill px-2", index === activeSlide ? "btn-dark" : "btn-outline-secondary")}
                onClick={() => goToIndex(index)}
              />
            ))}
          </div>
          <div className="d-flex justify-content-between mt-3">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={previousSlide}>
              <i className="ri-arrow-left-line me-1" /> Anterior
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={nextSlide}>
              Siguiente <i className="ri-arrow-right-line ms-1" />
            </button>
          </div>
        </>
      ) : (
        renderChartsRow(chartsToRender)
      )}
    </>
  );
};

export default ChartTabsSection;
